import { ApplicationConfig, DEFAULT_CURRENCY_CODE, importProvidersFrom, LOCALE_ID, provideZoneChangeDetection, inject, provideAppInitializer } from "@angular/core";
import {HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi} from "@angular/common/http";
import {
  BrowserModule,
  HammerModule, provideClientHydration, withEventReplay, withHttpTransferCacheOptions,
} from "@angular/platform-browser";
import {
  PreloadAllModules, provideRouter,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withPreloading,
  withRouterConfig
} from "@angular/router";
import {routes, defaultConfig} from "./app.routes";
import {NgbModule} from "@ng-bootstrap/ng-bootstrap";
import {DateAdapter, MAT_DATE_FORMATS, NativeDateAdapter} from "@angular/material/core";
import {LoginInterceptorService} from "./core/components/login/login-interceptor.service";
import {VHostService} from "./core/services/v-host.service";
import {ConfigService} from "./core/services/config.service";
import {DatePipe, registerLocaleData} from "@angular/common";
import localeGermany from '@angular/common/locales/de';
import localeNederland from '@angular/common/locales/nl';
import localeEnglish from '@angular/common/locales/en';
import {UniversalInterceptor} from "./core/services/universal-interceptor.service";
import {GuestAccountInterceptor} from "./core/services/guest-account-interceptor.service";
import {provideAnimations} from "@angular/platform-browser/animations";
import {CustomDateFormat} from "./core/models/custom-date-format";
import {TranslationService} from "./core/services/translation.service";

registerLocaleData(localeGermany);
registerLocaleData(localeNederland);
registerLocaleData(localeEnglish);

export function vHostFactory( service: VHostService) {
  return () => service.loadVhosts();
}

export function initLocale(configService: ConfigService) {
  return () => configService.initLocale();
}

// Basis-/Layout-Konfiguration app-weit vor dem ersten Render laden (SSR + Browser), damit
// app.component auf jeder Route korrekt ausgerichtet ist – auch auf Routen ohne eigenen
// ConfigResolverService (z. B. /offer, /offer/list).
export function initBaseConfig(configService: ConfigService) {
  return () => configService.loadConfigs(defaultConfig);
}

export function initDefaultCountry(configService: ConfigService) {
  return () => configService.initDefaultCountry();
}

export const appConfig: ApplicationConfig = {
  providers: [
    {provide: DateAdapter, useClass: NativeDateAdapter},
    {
      provide: MAT_DATE_FORMATS,
      useFactory: (translationService: TranslationService) => new CustomDateFormat(translationService),
      deps: [TranslationService]
    },
    DatePipe,
    importProvidersFrom(
      BrowserModule,
      HammerModule,
      NgbModule,
    ),
    provideZoneChangeDetection({eventCoalescing: true}),
    provideRouter(routes,
      withEnabledBlockingInitialNavigation(),
      withPreloading(PreloadAllModules),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled'
      }),
      withRouterConfig({
        onSameUrlNavigation: 'reload'
      })),
    provideClientHydration(
      withEventReplay(),
      withHttpTransferCacheOptions({
      includePostRequests: true,
      includeRequestsWithAuthHeaders: true
    })),
    provideHttpClient(
      withInterceptorsFromDi(),
      withFetch()
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UniversalInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoginInterceptorService,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: GuestAccountInterceptor,
      multi: true
    },
    {
      provide: LOCALE_ID,
      useValue: 'de'
    },
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: 'EUR'
    },
    VHostService,
    provideAppInitializer(() => {
        const initializerFn = (vHostFactory)(inject(VHostService));
        return initializerFn();
      }),
    provideAppInitializer(() => {
        const initializerFn = (initBaseConfig)(inject(ConfigService));
        return initializerFn();
      }),
    provideAppInitializer(() => {
        const initializerFn = (initLocale)(inject(ConfigService));
        return initializerFn();
      }),
    provideAppInitializer(() => {
        const initializerFn = (initDefaultCountry)(inject(ConfigService));
        return initializerFn();
      }),
    provideAnimations()
  ]
}
